'use client'

import classnames from 'classnames'
import styles from './ContentWithStickyNavigationMobileMenu.module.scss'
import useStore, { StickyMobileNavActiveItemsByIdType, StickyMobileNavContentType } from '@/store'
import { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Icon from '@/components/Icon/Icon'
import { ScrollContext } from '@/context/Scroll'
import useBreakpoint, { BREAKPOINTS } from '@/hooks/use-breakpoint'
import useWindowResize from '@/hooks/use-window-resize'
import { NAV_ID } from '@/components/Navigation/Navigation'
import {
  getStickyNavScrollTarget,
  measureStickyNavHeaderOffset,
} from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'

const ContentWithStickyNavigationMobileMenu = ({
  items,
  container,
  bottomNavContent,
  sectionBackground,
}: StickyMobileNavContentType) => {
  const [activeId, setActiveId] = useState('')
  const unsubRef = useRef<ReturnType<typeof useStore.subscribe> | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { scroll } = useContext(ScrollContext)
  const { breakpoint } = useBreakpoint()
  const isAllowedBreakpoint = !breakpoint?.width || breakpoint.width < parseInt(BREAKPOINTS.laptop)
  const [offsets, setOffsets] = useState<{ top: number; bottom: number }>({ top: 0, bottom: 0 })
  const resizeKey = useWindowResize()
  const [scrollState, setScrollingState] = useState<'start' | 'fixed' | 'end'>('start')
  const { onScrollCallback } = useContext(ScrollContext)
  const [scrollCallbackId, setScrollCallbackId] = useState('')
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const stickyBannerIsOpen = useStore(state => state.stickyBannerIsOpen)
  const isPinningRef = useRef(false)

  useEffect(() => {
    setScrollCallbackId(`id_${Math.random()}`)
  }, [])

  const calculateOffsets = useCallback(() => {
    if (!container) return
    const offsetTop = container.getBoundingClientRect().top + document.documentElement.scrollTop
    const offsetBottom = offsetTop + container.offsetHeight

    setOffsets({
      top: offsetTop,
      bottom: offsetBottom,
    })
  }, [setOffsets, container])

  const handleNavigationClick = (id: string) => {
    const listElement = document.getElementById(id)
    if (!id || !scroll || !listElement) return

    scroll.start()
    setIsOpen(false)

    setTimeout(() => {
      if (listElement instanceof HTMLElement) {
        const clearance = measureStickyNavHeaderOffset()
        // On mobile the sticky button bar already shows the active section's name, so the
        // section's own title under it is redundant. Scroll past the title so the section
        // content (e.g. dish grid) lands directly under the sticky bar and the title hides
        // behind it.
        const titleEl = listElement.querySelector('h1, h2, h3, h4, h5, h6')
        const titleSkip =
          titleEl instanceof HTMLElement
            ? titleEl.offsetHeight + (parseFloat(getComputedStyle(titleEl).marginBottom) || 0)
            : 0
        const offset = getStickyNavScrollTarget(listElement, clearance) + titleSkip
        scroll.scrollTo(offset + 1, { duration: 0.6 })
      }
    }, 250)
  }

  // The panel is sized for the bar sitting under the header ('fixed'). When the bar is still in
  // the page flow, opening it there leaves most of the panel below the screen with page scroll
  // locked, so scroll the bar into its stuck position first and open once it's there.
  const handleHeaderButtonClick = () => {
    if (isPinningRef.current) return

    if (isOpen) {
      setIsOpen(false)
      return
    }

    // Same header measurement as the scroll-state callback below, so the target is guaranteed
    // to land in the 'fixed' range.
    const headerHeight = document.getElementById(NAV_ID)?.offsetHeight ?? 0
    const pinTarget =
      scrollState === 'start'
        ? // Lenis rounds targets; round up so a fractional offset can't stop short of 'fixed'.
          Math.ceil(offsets.top - headerHeight)
        : scrollState === 'end'
          ? offsets.bottom - headerHeight - 1
          : null

    // Lenis ignores scrollTo (and never calls onComplete) while stopped, so just open.
    if (pinTarget === null || !scroll || scroll.isStopped) {
      setIsOpen(true)
      return
    }

    isPinningRef.current = true
    scroll.scrollTo(pinTarget, {
      duration: 0.4,
      lock: true,
      onComplete: () => {
        isPinningRef.current = false
        setIsOpen(true)
      },
    })
  }

  useEffect(() => {
    return () => {
      onScrollCallback({
        key: scrollCallbackId,
        remove: true,
      })
    }
  }, [scrollCallbackId, onScrollCallback])

  useEffect(() => {
    if ((offsets.top === 0 && offsets.bottom === 0) || !onScrollCallback || !scrollCallbackId) return

    const headerElement = document.getElementById(NAV_ID)
    const headerHeight = headerElement?.offsetHeight
    if (!headerHeight) return

    onScrollCallback({
      key: scrollCallbackId,
      remove: true,
    })

    onScrollCallback({
      key: scrollCallbackId,
      callback: e => {
        const currentScroll = e.animatedScroll
        if (currentScroll < offsets.top - headerHeight) {
          setScrollingState('start')
        } else if (currentScroll > offsets.bottom - headerHeight) {
          setScrollingState('end')
        } else {
          setScrollingState('fixed')
        }
      },
    })
  }, [scroll, offsets, onScrollCallback, scrollCallbackId])

  useEffect(() => {
    calculateOffsets()
  }, [calculateOffsets, resizeKey])

  useEffect(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.unobserve(document.body)
    }

    // create an Observer instance
    resizeObserverRef.current = new ResizeObserver(() => {
      calculateOffsets()
    })

    // start observing a DOM node
    resizeObserverRef.current.observe(document.body)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.unobserve(document.body)
      }
    }
  }, [calculateOffsets])

  useEffect(() => {
    if (!scroll) return
    scroll[isOpen && isAllowedBreakpoint ? 'stop' : 'start']()
  }, [isOpen, scroll, isAllowedBreakpoint])

  useEffect(() => {
    if (unsubRef.current) unsubRef.current()

    if (isAllowedBreakpoint) {
      unsubRef.current = useStore.subscribe(
        state => [state.stickyMobileNavActiveItemsById],
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        (value: any) => {
          const itemsById = value[0]
          if (itemsById) {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            const activeId = itemsById.find(
              (item: StickyMobileNavActiveItemsByIdType) => item.id === container.id,
            )?.activeId
            setActiveId(activeId || items[0].id)
          }
        },
      )
    }

    return () => {
      if (unsubRef.current) {
        unsubRef.current()
      }
    }
  }, [setActiveId, container.id, items, isAllowedBreakpoint])

  if (!activeId || !items.length || !container || !isAllowedBreakpoint) return null

  return (
    <div
      className={classnames(
        styles.ContentWithStickyNavigationMobileMenu,
        { [styles.isOpen]: isOpen },
        { [styles.stickyBannerIsOpen]: stickyBannerIsOpen },
      )}
      data-theme={sectionBackground}
      data-scrolling-state={scrollState}
      style={
        {
          '--background-color': `var(--${sectionBackground})`,
          '--offset-top': `${offsets.top}px`,
          '--offset-bottom': `${offsets.bottom}px`,
        } as CSSProperties
      }
    >
      {isOpen && (
        // Catches taps outside the open panel so they close it instead of hitting the page.
        <div
          className={styles.backdrop}
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      <button
        onClick={handleHeaderButtonClick}
        className={styles.headerButton}
      >
        <span className={styles.headerButton__text}>{items.find(item => item.id === activeId)?.title}</span>
        <Icon
          name="caretDown"
          className={styles.headerButton__icon}
        />
      </button>
      <div className={styles.content}>
        <div
          className={styles.contentInner}
          data-lenis-prevent
        >
          <ul className={styles.linkList}>
            {items.map((item, i) => (
              <li
                key={i}
                className={classnames(styles.linkList__item, {
                  [styles.isActive]: activeId === item.id,
                })}
              >
                <button
                  onClick={() => {
                    handleNavigationClick(item.id)
                  }}
                  className={styles.linkList__itemButton}
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
          {bottomNavContent && <div className={styles.bottomContent}>{bottomNavContent}</div>}
        </div>
      </div>
    </div>
  )
}

ContentWithStickyNavigationMobileMenu.displayName = 'ContentWithStickyNavigationMobileMenu'

export default ContentWithStickyNavigationMobileMenu
